// Public interview API client. These endpoints are unauthenticated (the backend
// mints the ElevenLabs WebRTC conversation token server-side), so we use plain
// fetch with no auth header.

const BASE: string = import.meta.env.VITE_BACKEND_URL ?? "";

/** A screener option as the participant sees it — qualification marks are stripped server-side. */
export interface PublicScreenerOption {
  id: string;
  label: string;
}

export interface PublicScreenerQuestion {
  id: string;
  question: string;
  type: "single_choice" | "multiple_choice";
  options: PublicScreenerOption[];
}

/** Participant's screener selections: question_id → selected option_ids. */
export type ScreenerAnswers = Record<string, string[]>;

/** How a participant's external ID is captured (drives the welcome page): `none` anonymous
 *  (screener applies); `manual` pasted on the welcome page; `url_query` read from a URL parameter. */
export type ParticipantIdSource = "none" | "manual" | "url_query";

/** A language this link offers, labelled with its endonym ("Deutsch", not "German"). */
export interface LanguageOption {
  code: string;
  label: string;
}

/** The participant-visible copy in one language. */
export interface LocalizedStudyCopy {
  external_title: string;
  external_description: string;
  screener: PublicScreenerQuestion[];
}

export interface PublicStudyInfo {
  /** False while launch-time welcome and screening generation is still finishing. */
  ready: boolean;
  /** Participant-facing copy shown on the welcome page (the study's external title/description). */
  external_title: string;
  external_description: string;
  /** Whether this link applies the screener (false → `screener` is empty and never gates). */
  apply_screener: boolean;
  screener: PublicScreenerQuestion[];
  participant_id_source: ParticipantIdSource;
  /** Query-param key holding the participant ID when source is `url_query` (e.g. participant_id). */
  participant_id_param: string | null;
  /** Optional completion redirect shown at the end (panel-recruited studies). */
  completion_url: string | null;
  /** Optional redirect for a participant who fails the screener (e.g. a panel's screen-out URL). */
  screenout_url: string | null;
  /** What the participant may pick before the call starts. Optional so a response from a backend
   *  that predates the picker (and the synthetic infos built for the researcher preview) still
   *  type-checks; a single language means no picker is shown. */
  languages?: LanguageOption[];
  default_language?: string;
  /** Every offered language's copy, keyed by code — sent up front so switching the picker
   *  re-renders instantly. A language whose translation isn't ready is simply absent, and the
   *  client falls back to the top-level (source-language) copy. */
  localized?: Record<string, LocalizedStudyCopy>;
  /** Signed, short-lived pin to the exact scope revision rendered on this welcome page. */
  scope_token?: string;
}

export interface StartInterviewResponse {
  session_id: string;
  conversation_token: string;
  /** Conversation-initiation data for the SDK. Studies on a provisioned per-revision moderator
   *  agent receive only the spoken copy (external_title, external_description); others still get
   *  the full brief. Passed through verbatim either way. */
  dynamic_variables: Record<string, string>;
  title: string;
  /** What the interview will actually run in. The server may have resolved this to something other
   *  than we asked for — a link that doesn't offer the requested language falls back to its default
   *  — so the UI must re-sync from this rather than trust its own pick. */
  language?: string;
  /** The value to pass to ElevenLabs as a conversation override, absent when none is needed (the
   *  agent's own primary language) or against a backend predating the picker. Send an override only
   *  when this is present: ElevenLabs errors on an override for a field the agent hasn't enabled.
   *  The voice is never overridden — it comes from the agent's language preset. */
  language_override?: string;
}

export class InterviewError extends Error {
  /** The backend's human-readable `detail` (e.g. "This interview has closed."), when present. */
  constructor(
    public status: number,
    public detail?: string,
    /** A stable machine-readable code the backend put beside the message
     *  (`{ detail: { code, message } }`), e.g. `moderator_not_ready`. Absent on plain-detail errors. */
    public code?: string
  ) {
    super(detail ?? `Interview request failed (${status})`);
  }
}

/** The start was refused because the study's moderator is not provisioned (yet): the backend's
 *  `InterviewModeratorNotReady`, a 503 keyed by this code. The page shows its own "not ready yet,
 *  try again in a few minutes" copy with a try-again button; it does not retry on its own, and
 *  every other 5xx stays the generic failure. */
export const MODERATOR_NOT_READY_CODE = "moderator_not_ready";

export function isModeratorNotReady(e: unknown): e is InterviewError {
  return e instanceof InterviewError && e.status === 503 && e.code === MODERATOR_NOT_READY_CODE;
}

interface ErrorBody {
  detail?: string;
  code?: string;
}

/** Pull the FastAPI `{ detail }` off an error response (best-effort; body may not be JSON).
 *  `detail` is either the message itself or `{ code, message }` for errors the client keys
 *  behaviour on; either way the message lands in `detail` and the code, if any, in `code`. */
async function errorBody(resp: Response): Promise<ErrorBody> {
  try {
    const body = (await resp.json()) as { detail?: unknown };
    const detail = body.detail;
    if (typeof detail === "string") return { detail };
    if (detail && typeof detail === "object") {
      const { code, message } = detail as { code?: unknown; message?: unknown };
      return {
        detail: typeof message === "string" ? message : undefined,
        code: typeof code === "string" ? code : undefined,
      };
    }
    return {};
  } catch {
    return {};
  }
}

async function toInterviewError(resp: Response): Promise<InterviewError> {
  const { detail, code } = await errorBody(resp);
  return new InterviewError(resp.status, detail, code);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, init);
  if (!resp.ok) throw await toInterviewError(resp);
  return (await resp.json()) as T;
}

function getJson<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function getPublicStudy(slug: string): Promise<PublicStudyInfo> {
  return getJson<PublicStudyInfo>(`/interviews/public/${encodeURIComponent(slug)}`);
}

/** `?language=…` for the admin preview routes, or "" when no language was picked. Those routes
 *  take the pick in the query string (they have no body); the public start sends it in the body. */
export function languageQuery(language?: string): string {
  return language ? `?language=${encodeURIComponent(language)}` : "";
}

/** `language` is the participant's pick. The server falls back to the link's default if this link
 *  doesn't offer it, rather than refusing to start. */
export function startPublicInterview(
  slug: string,
  scopeToken: string,
  screenerAnswers: ScreenerAnswers = {},
  participantId?: string,
  metadata: Record<string, string> = {},
  consent = false,
  language?: string,
  /** Key for this start attempt; resending it after a lost response returns the same session. */
  attemptId?: string
): Promise<StartInterviewResponse> {
  return request<StartInterviewResponse>(`/interviews/public/${encodeURIComponent(slug)}/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      screener_answers: screenerAnswers,
      participant_id: participantId,
      metadata,
      consent,
      language,
      scope_token: scopeToken,
      ...(attemptId ? { attempt_id: attemptId } : {}),
    }),
  });
}

/** Check one screener answer so the participant flow can stop at the first failing question.
 *  Returns just a boolean — qualification marks stay server-side. The authoritative gate still
 *  runs at start (all answers re-evaluated before a token is minted). */
export function checkScreenerAnswer(
  slug: string,
  scopeToken: string,
  questionId: string,
  selected: string[]
): Promise<boolean> {
  return request<{ qualified: boolean }>(
    `/interviews/public/${encodeURIComponent(slug)}/screen`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question_id: questionId, selected, scope_token: scopeToken }),
    },
  ).then((r) => r.qualified);
}

export interface FeedbackInput {
  /** 1–5 star rating; omit to update only the note. */
  rating?: number;
  /** Free-text note; omit to update only the rating. */
  feedback?: string;
}

/** Save the participant's post-call feedback (rating on star-click, note on submit).
 *  The endpoint returns 204, so there's no body to parse. */
export async function submitInterviewFeedback(
  sessionId: string,
  input: FeedbackInput
): Promise<void> {
  const resp = await fetch(
    `${BASE}/interviews/sessions/${encodeURIComponent(sessionId)}/feedback`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  if (!resp.ok) throw await toInterviewError(resp);
}
